import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { ShieldCheck, ExternalLink, Save } from "lucide-react";
import { toast } from "sonner";

export const BillingConfig = ({ open, onOpenChange, onSaved }) => {
  const [cfg, setCfg] = useState(null);
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/billing/config").then((r) => { setCfg(r.data); setLink(r.data.payment_link_url || ""); }).catch(() => {});
  }, [open]);

  const save = async () => {
    if (link && !/^https?:\/\//i.test(link)) {
      toast.error("URL must start with https://");
      return;
    }
    setBusy(true);
    try {
      await api.post("/billing/config", { payment_link_url: link });
      toast.success("Pay-by-Link saved");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-xl border-black/10" data-testid="billing-config-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#E60000]" /> Chase QuickAccept · Pay-by-Link
          </DialogTitle>
          <DialogDescription className="text-xs uppercase tracking-[0.2em] text-[#E60000] font-bold">
            Owner only · used by every member's "Pay via Chase" button
          </DialogDescription>
        </DialogHeader>

        <ol className="text-sm text-black/65 space-y-1.5 list-decimal list-inside">
          <li>Open Chase Business Online → <strong>QuickAccept</strong>.</li>
          <li>Tap <strong>Request a Payment</strong> → <em>Pay-by-Link</em>.</li>
          <li>Set the amount + memo "Monster Squad premium" → copy the link.</li>
          <li>Paste it below. Members will be redirected to that Chase-hosted page.</li>
        </ol>

        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/60">Pay-by-Link URL</label>
          <Input
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="https://secure.chase.com/web/quickaccept/pay/…"
            className="mt-1 rounded-sm h-11 font-mono text-sm"
            data-testid="billing-link-input"
          />
          {cfg?.business && (
            <div className="mt-3 text-[10px] uppercase tracking-[0.2em] font-mono text-black/40">
              Descriptor: {cfg.business.legal_name} dba {cfg.business.dba} · env: {cfg.business.env}
            </div>
          )}
        </div>

        <div className="flex justify-between items-center pt-2">
          {link && (
            <a href={link} target="_blank" rel="noopener noreferrer" className="text-xs text-black/50 hover:text-[#E60000] flex items-center gap-1" data-testid="billing-link-test">
              Test link <ExternalLink className="w-3 h-3" />
            </a>
          )}
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="billing-cancel">Cancel</Button>
            <Button onClick={save} disabled={busy} className="rounded-sm bg-[#E60000] hover:bg-[#CC0000] text-white" data-testid="billing-save">
              <Save className="w-4 h-4 mr-2" /> {busy ? "Saving…" : "Save link"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
